import React, { Suspense, lazy } from "react";

import { Navigate, useOutletContext } from "react-router-dom";

import { useBootstrap } from "./app.bootstrap";

import { type ResolvedProps } from "./app.types";

const LazyAdminModule = lazy(() => import("./admin/AdminModule"));
const LazyMapModule = lazy(() => import("./map/map.module"));
const LazyUserModule = lazy(() => import("./core/user/UserModule"));
const LazyNotificationsModule = lazy(
  () => import("./notifications/NotificationsModule"),
);
const LazySensorsModule = lazy(() => import("./sensors/SensorsModule"));
const LazyReportsModule = lazy(
  () => import("./evidence/reports/ReportsModule"),
);
const LazyActionsModule = lazy(
  () => import("./evidence/actions/ActionsModule"),
);
const LazyCataloguesModule = lazy(
  () => import("./catalogues/containers/CataloguesModule"),
);
const LazyStoresModule = lazy(() => import("./evidence/stores/StoresModule"));
const LazyTelematicsModule = lazy(
  () => import("./telematics/TelematicsModule"),
);
const LazyParcelsModule = lazy(() => import("./core/parcels/ParcelsModule"));
const LazyPrecisionFarmingModule = lazy(
  () => import("./precision-farming/PrecisionFarmingModule"),
);
const IrrigationModule = lazy(() => import("./irrigation/IrrigationModule"));
const LazyOAuthModule = lazy(() => import("./oauth/OAuthModule"));
const LazyNdwiReport = lazy(
  () => import("./precision-farming/routes/ndwi-report/ndwi-report"),
);

const withSuspense =
  (text: string, Comp: React.ComponentType<ResolvedProps>) => () => {
    const props = useResolvedProps();
    return (
      <Suspense fallback={<div>{text}</div>}>
        <Comp {...props} />
      </Suspense>
    );
  };

const ActionsRoute = withSuspense("Loading actions...", LazyActionsModule);
const ReportsRoute = withSuspense("Loading reports...", LazyReportsModule);
const NdwiReportRoute = withSuspense("Loading report...", LazyNdwiReport);
const UserRoute = withSuspense("Loading user...", LazyUserModule);
const MapRoute = withSuspense("Loading map...", LazyMapModule);
const AdminRoute = withSuspense("Loading admin...", LazyAdminModule);
const NotificationsRoute = withSuspense(
  "Loading notifications...",
  LazyNotificationsModule,
);
const SensorsRoute = withSuspense("Loading sensors...", LazySensorsModule);
const CataloguesRoute = withSuspense(
  "Loading catalogues...",
  LazyCataloguesModule,
);
const IrrigationRoute = withSuspense("Loading irrigation...", IrrigationModule);
const StoresRoute = withSuspense("Loading stores...", LazyStoresModule);
const TelematicsRoute = withSuspense(
  "Loading telematics...",
  LazyTelematicsModule,
);
const ParcelsRoute = withSuspense("Loading parcels...", LazyParcelsModule);
const PrecisionFarmingRoute = withSuspense(
  "Loading precision farming...",
  LazyPrecisionFarmingModule,
);

const useResolvedProps = () => useOutletContext<ResolvedProps>();

const FarmIndexRedirect = () => {
  const { farms } = useBootstrap();
  return <Navigate replace to={`/farm/${farms[0]!.id}`} />;
};

const FarmIdRootRoute = () => {
  const props = useResolvedProps();
  return <Navigate replace to={`/farm/${props.farm.id}/map`} />;
};

export {
  ActionsRoute,
  AdminRoute,
  CataloguesRoute,
  FarmIdRootRoute,
  FarmIndexRedirect,
  IrrigationRoute,
  LazyOAuthModule,
  MapRoute,
  NdwiReportRoute,
  NotificationsRoute,
  ParcelsRoute,
  PrecisionFarmingRoute,
  ReportsRoute,
  SensorsRoute,
  StoresRoute,
  TelematicsRoute,
  UserRoute,
};
